import { Outlet, RouterProvider, createBrowserRouter } from '@suntex.core/nav';
import { NavigationProvider } from '@suntex.fim/player';

const EMPTY_ROUTER_PREFIXES: readonly string[] = [];

/**
 * Wraps a component for standalone routing within the classic FIM app.
 *
 * Mounts `Component` at the index of `basePath` and, optionally, mounts
 * `WildcardFallback` for any other sub-path (e.g. legacy/short links that
 * need translating before reaching `Component`). When `WildcardFallback` is
 * omitted, `Component` itself handles all sub-paths, preserving prior
 * behaviour for consumers that don't need a fallback.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function wrapForRouting<C extends React.FunctionComponent<any>>(
  Component: C,
  basePath: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  WildcardFallback?: React.FunctionComponent<any>,
) {
  return (props: React.ComponentProps<C>): JSX.Element => {
    const router = createBrowserRouter([
      {
        path: `/${basePath}`,
        element: (
          <NavigationProvider routerPrefixes={EMPTY_ROUTER_PREFIXES}>
            <Outlet />
          </NavigationProvider>
        ),
        children: [
          { index: true, element: <Component {...props} /> },
          {
            path: '*',
            element: WildcardFallback ? (
              <WildcardFallback />
            ) : (
              <Component {...props} />
            ),
          },
        ],
      },
    ]);
    return <RouterProvider router={router} />;
  };
}
